import { useAppSelector } from '@/store/hooks';
import {
  type Dv360LiveReachBreakdown,
  type Dv360LiveReachRow,
  postBeeswaxLiveReach,
  postDv360LiveReach,
  postGamLiveReach,
} from '@/store/slices/reportingV2/reportingV2.api';
import {
  type ReportingV2Filters,
  selectReportingV2Filters,
  selectReportingV2Freshness,
} from '@/store/slices/reportingV2/reportingV2Slice';
import { useEffect, useState } from 'react';
import { comparisonWindowEnd } from './usePriorPeriod';
import { useReportingPermissions } from './useReportingPermissions';

const POLL_MS = 3_000;
const MAX_POLLS = 60;

export type LiveReachPlatform = 'dv360' | 'beeswax' | 'gam';

export const LIVE_REACH_PLATFORM_LABELS: Record<LiveReachPlatform, string> = {
  dv360: 'DV360',
  beeswax: 'Beeswax',
  gam: 'GAM',
};

export interface LiveReachNotes {
  note: string;
  withheld: string;
  partial: string;
  failed: string;
}

export const LIVE_REACH_NOTES: Record<LiveReachPlatform, LiveReachNotes> = {
  dv360: {
    note: 'Deduplicated reach for the whole period, straight from DV360 (lower-certainty estimate).',
    withheld:
      "DV360 can't give one reach figure for this row (too little volume, or several DV360 TV device types combined).",
    partial:
      'DV360 live reach covers only part of these impressions (some delivery is not linked to DV360), so it is not shown.',
    failed: "DV360 live reach couldn't be loaded. Try again later.",
  },
  beeswax: {
    note: 'Deduplicated reach for the whole period: distinct IP addresses (households) from Beeswax impression data, ±2%.',
    withheld: 'Beeswax reports no reach for this row.',
    partial:
      'Beeswax live reach covers only part of these impressions (some delivery is not linked to Beeswax), so it is not shown.',
    failed: "Beeswax live reach couldn't be loaded. Try again later.",
  },
  gam: {
    note: 'Deduplicated reach for the whole period, straight from Google Ad Manager.',
    withheld: 'Google Ad Manager reports no reach for this row.',
    partial:
      'GAM live reach covers only part of these impressions (some delivery is not linked to GAM), so it is not shown.',
    failed: "GAM live reach couldn't be loaded. Try again later.",
  },
};

/** GAM reach reports can't group by campaign id or device, so GAM is live only for these. */
export const LIVE_REACH_BREAKDOWNS: Record<LiveReachPlatform, readonly Dv360LiveReachBreakdown[]> = {
  dv360: ['total', 'campaign', 'device', 'day'],
  beeswax: ['total', 'campaign', 'device', 'day'],
  gam: ['total', 'day'],
};
const GENERIC_NOTES_BASE: Omit<LiveReachNotes, 'note'> = {
  withheld: 'The platform reports no reach for this row.',
  partial:
    'Live reach covers only part of these impressions (some delivery is not linked to the platform), so it is not shown.',
  failed: "Live reach couldn't be loaded. Try again later.",
};
const PLATFORM_REACH_NOTE = 'Deduplicated reach for the whole period, straight from the platform.';
/**
 * The same notes without the platform name, for a viewer without canViewPlatform. Each note keeps
 * what reach counts and how exact it is, so household reach is not read as people.
 */
export const GENERIC_LIVE_REACH_NOTES: Record<LiveReachPlatform, LiveReachNotes> = {
  dv360: {
    ...GENERIC_NOTES_BASE,
    note: 'Deduplicated reach for the whole period, straight from the platform (lower-certainty estimate).',
  },
  beeswax: {
    ...GENERIC_NOTES_BASE,
    note: 'Deduplicated reach for the whole period: distinct IP addresses (households) from impression data, ±2%.',
  },
  gam: { ...GENERIC_NOTES_BASE, note: PLATFORM_REACH_NOTE },
};

/** Notes and label a viewer may see: platform names only with canViewPlatform. */
export const liveReachCopy = (platform: LiveReachPlatform | null, canViewPlatform: boolean) =>
  canViewPlatform
    ? { notes: LIVE_REACH_NOTES[platform ?? 'dv360'], label: LIVE_REACH_PLATFORM_LABELS[platform ?? 'dv360'] }
    : { notes: GENERIC_LIVE_REACH_NOTES[platform ?? 'dv360'], label: 'live' };

export const LIVE_REACH_NOTE = LIVE_REACH_NOTES.dv360.note;
export const LIVE_REACH_WITHHELD_NOTE = LIVE_REACH_NOTES.dv360.withheld;
export const LIVE_REACH_PARTIAL_NOTE = LIVE_REACH_NOTES.dv360.partial;
export const LIVE_REACH_FAILED_NOTE = LIVE_REACH_NOTES.dv360.failed;

const POST_LIVE_REACH: Record<LiveReachPlatform, typeof postDv360LiveReach> = {
  dv360: postDv360LiveReach,
  beeswax: postBeeswaxLiveReach,
  gam: postGamLiveReach,
};

/**
 * How far DV360 and warehouse impressions may differ before a live value is withheld.
 * Restatement noise stays well under 2%; Daniel suggested tuning toward 10% (flank#4479).
 */
const configuredTolerance = Number(import.meta.env.VITE_DV360_LIVE_REACH_TOLERANCE);
export const COVERAGE_TOLERANCE =
  Number.isFinite(configuredTolerance) && configuredTolerance > 0 ? configuredTolerance : 0.02;

/**
 * A live value describes only the delivery DV360 returned for the linked advertisers. Show it
 * only when that is the same delivery as the UR impressions beside it, or reach and frequency
 * would describe a different (often much smaller) audience than the row.
 */
export const liveCovers = (urImpressions: unknown, live: Dv360LiveReachRow | undefined): boolean => {
  const ur = Number(urImpressions ?? 0);
  if (!live || !(ur > 0)) return false;
  return Math.abs(Number(live.impressions ?? 0) - ur) / ur <= COVERAGE_TOLERANCE;
};

/**
 * Live reach replaces the summed canonical reach only where the view is one live platform alone
 * and every filter is one the endpoint can narrow by — otherwise the live figure would
 * describe a different set of delivery than the rows beside it.
 */
export const liveReachPlatform = (filters: ReportingV2Filters): LiveReachPlatform | null =>
  filters.dsps.length === 1 &&
  (filters.dsps[0] === 'dv360' || filters.dsps[0] === 'beeswax' || filters.dsps[0] === 'gam') &&
  ![
    filters.ribeyeCampaignIds,
    filters.ribeyeLineItemIds,
    filters.productIds,
    filters.channelIds,
    filters.deviceIds,
    filters.publishers,
  ].some(values => values.length > 0)
    ? (filters.dsps[0] as LiveReachPlatform)
    : null;

export const isDv360LiveReachScope = (filters: ReportingV2Filters): boolean => liveReachPlatform(filters) === 'dv360';

/** Normalises a row's UR dimension value to the live response key (days arrive with a time part). */
export const liveReachKey = (breakdown: Dv360LiveReachBreakdown, value: unknown): string | null =>
  value == null ? null : breakdown === 'day' ? String(value).slice(0, 10) : String(value);

export interface Dv360LiveReach {
  /** True when live values should replace the canonical reach/frequency. */
  active: boolean;
  platform: LiveReachPlatform | null;
  notes: LiveReachNotes;
  /** Platform name for UI copy, or a generic word when the viewer can't see platforms. */
  label: string;
  loading: boolean;
  failed: boolean;
  byKey: Map<string | null, Dv360LiveReachRow>;
}

/**
 * Polls the platform's live reach endpoint until it has answered (DV360 5–25 s, Beeswax ~40 s,
 * GAM 5–20 s; cached after).
 * `dates` overrides the filter window, for the prior-period comparison.
 */
export const useDv360LiveReach = (
  breakdown: Dv360LiveReachBreakdown,
  { enabled = true, dates }: { enabled?: boolean; dates?: { start: string; end: string } } = {},
): Dv360LiveReach => {
  const filters = useAppSelector(selectReportingV2Filters);
  const { canViewPlatform } = useReportingPermissions();
  const freshness = useAppSelector(selectReportingV2Freshness);
  const platform = liveReachPlatform(filters);
  const active = enabled && platform !== null && LIVE_REACH_BREAKDOWNS[platform].includes(breakdown);
  const range = dates ?? { start: filters.dateStart, end: filters.dateEnd };
  // Freshness arrives with the first widget response; asking before then would query an
  // unclamped window that can never match, then query again once it lands.
  const freshnessReady = Object.keys(freshness).length > 0;
  const [byKey, setByKey] = useState<Map<string | null, Dv360LiveReachRow>>(new Map());
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const request = JSON.stringify({
    // Stop at the last day the warehouse holds for the platform: the platform itself is always
    // ahead, and asking for days the UR rows don't have would never match their impressions.
    date_range: {
      start: range.start,
      end: comparisonWindowEnd(range.start, range.end, [platform ?? 'dv360'], freshness),
    },
    breakdown,
    ...(filters.whitelabelIds.length ? { whitelabel_ids: filters.whitelabelIds } : {}),
    ...(filters.clientIds.length ? { client_ids: filters.clientIds } : {}),
    ...(filters.advertiserIds.length ? { advertiser_ids: filters.advertiserIds } : {}),
  });

  useEffect(() => {
    setByKey(new Map());
    setFailed(false);
    if (!active || !freshnessReady) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setLoading(true);

    const poll = async (attempt: number) => {
      try {
        const response = await POST_LIVE_REACH[platform ?? 'dv360'](JSON.parse(request));
        if (cancelled) return;
        if (response.status === 'pending' && attempt < MAX_POLLS) {
          timer = setTimeout(() => poll(attempt + 1), POLL_MS);
          return;
        }
        if (response.status === 'done') {
          setByKey(new Map(response.rows.map(row => [row.key, row])));
        } else if (response.status !== 'unavailable') {
          // `unavailable` means nothing in scope is linked to DV360: no live rows, so cells read
          // as partial coverage. Anything else is an outage or a timeout.
          setFailed(true);
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
      if (!cancelled) setLoading(false);
    };
    poll(0);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [active, platform, freshnessReady, request]);

  return {
    active,
    platform,
    ...liveReachCopy(platform, canViewPlatform),
    loading: loading || (active && !freshnessReady),
    failed,
    byKey,
  };
};
